<template>
  <!-- #region template -->
  <div class="h-100 d-flex flex-column justify-content-between">
    <div>
      <h5 class="font-weight-medium m-0 text-dark">{{ title }}</h5>
      <template v-if="compare">
        <div class="d-flex justify-content-end align-items-center">
          <div class="performance d-flex align-items-center" :class="change">
            <template v-if="performance > 0">
              <b-icon class="icon" icon="arrow-up"></b-icon>
            </template>
            <template v-else-if="performance < 0">
              <b-icon class="icon" icon="arrow-down"></b-icon>
            </template>
            <template v-else>
              <b-icon class="icon" icon="dash"></b-icon>
            </template>
            <p class="m-0">{{ Math.abs(performance || 0) }}%</p>
          </div>
        </div>
      </template>
      <div class="my-3">
        <slot></slot>
      </div>
    </div>
    <div>
      <template v-if="!empty">
        <template v-if="type == ChartType.Bar">
          <BarChart ref="chart" />
        </template>
        <template v-if="type == ChartType.Line">
          <LineChart ref="chart" />
        </template>
        <template v-if="type == ChartType.Pie">
          <PieChart ref="chart" />
        </template>
      </template>
      <template v-else>
        <p>Sorry no data available for the current query</p>
      </template>
    </div>
  </div>
  <!-- #endregion -->
</template>

<script>
// #region script
// #region import
import BarChart from '@/components/chartsjs/BarChart'
import LineChart from '@/components/chartsjs/LineChart'
import PieChart from '@/components/chartsjs/PieChart'
import { ChartType } from '@/utils'
// #endregion

// #region export
export default {
  name: 'cChart',
  components: {
    BarChart,
    LineChart,
    PieChart,
  },
  props: {
    title: {
      type: String,
      default: 'Title',
    },
    type: {
      type: Symbol,
    },
    compare: {
      type: Boolean,
      default: false,
    },
    current: {
      type: Array,
      default() {
        return []
      },
    },
    previous: {
      type: Array,
      default() {
        return []
      },
    },
    options: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chartData: {
        labels: [],
        datasets: [
          {
            label: '',
            data: [],
            backgroundColor: '#607d8b',
          },
          {
            label: '',
            data: [],
            backgroundColor: '#e1e7ea',
          },
        ],
      },
      chartOptions: {
        scales: {
          xAxes: [
            {
              ticks: {
                // maxRotation: 0,
                // minRotation: 0,
                callback: function (tick) {
                  if (tick.length > 10) {
                    return tick.substring(0, 10) + '...'
                  } else {
                    return tick
                  }
                },
              },
            },
          ],
        },
        legend: {
          position: 'bottom',
          align: 'end',
        },
        responsive: true,
        maintainAspectRatio: false,
      },
      ChartType,
      performance: 0,
      empty: false,
    }
  },
  computed: {
    /**
     * Gets the style for the performance.
     */
    change() {
      return this.performance > 0 ? 'positive' : this.performance < 0 ? 'negative' : 'positive'
    },
  },
  methods: {
    /**
     * Renders (or render if already rendered) the chart with the current props.
     */
    renderChart() {
      this.chartData.labels = []
      this.chartData.datasets[0].data = []
      this.chartData.datasets[1].data = []

      let current = this.tally([...this.current])
      let previous = this.tally([...this.previous])

      let currentCount = 0
      let previousCount = 0
      for (const [key, value] of Object.entries(current)) {
        this.chartData.labels.push(this.options.label(key) || 'Other')
        this.chartData.datasets[0].data.push(value)
        let value2 = previous[key] || 0
        if (previous[key]) {
          delete previous[key]
        }
        currentCount += value
        previousCount += value2
        this.chartData.datasets[1].data.push(value2)
      }
      for (const [key, value] of Object.entries(previous)) {
        this.chartData.labels.push(this.options.label(key) || 'Other')
        this.chartData.datasets[1].data.push(value)
        previousCount += value
        this.chartData.datasets[0].data.push(0)
      }
      this.chartData.datasets[0].label = this.options.labels.current
      this.chartData.datasets[1].label = this.options.labels.previous

      this.performance = (currentCount - previousCount) / previousCount
      this.performance = (this.performance * 100).toFixed(1)
      if (this.performance == 'Infinity' || isNaN(this.performance)) {
        this.performance = 0
      }
      if (this.chartData.datasets[0].data.length == 0 && this.chartData.datasets[1].data.length == 0) {
        this.empty = true
      } else {
        this.empty = false
        this.$nextTick(() => {
          this.$refs['chart'].renderChart(this.chartData, this.chartOptions)
        })
      }
    },
    /**
     * Tally a dataset.
     */
    tally(dataset) {
      this.options.handlers.forEach((handle) => {
        dataset = dataset.filter(Boolean)
        dataset = dataset.filter((value) => {
          return handle.filter(value)
        })
        dataset = dataset.map((value) => {
          return handle.map(value)
        })
        dataset = dataset.flat()
      })
      if (this.options.sort) {
        dataset = this.options.sort(dataset)
      }

      let tally = {}
      dataset.forEach((data) => {
        if (!tally[data]) {
          tally[data] = 0
        }
        tally[data]++
      })
      return tally
    },
  },
}
// #endregion
// #endregion
</script>

<style scoped lang="scss">
// #region style
.performance {
  .b-icon {
    font-size: 100%;
  }
  p {
    font-size: 100%;
  }
  &.positive {
    color: green;
  }
  &.negative {
    color: red;
  }
}
// #endregion
</style>
